import { KPICard } from '@/components/data-display';
import { Button } from '@/components/common/Button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import type { ApiError } from '@/lib/api-client';
import { useRevenue } from './useRevenue';
import { RevenueCharts } from './RevenueCharts';
import { RevenueBreakdown } from './RevenueBreakdown';
import type { RevenueDateRange } from './types';

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatCurrency(value: number): string {
  return (
    '$' +
    value.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
  );
}

// ---------------------------------------------------------------------------
// Error → toast copy resolver
//
// Prefers the normalised status code from api-client's response interceptor
// (`ApiError.statusCode`); falls back to message-string heuristics only when
// statusCode is undefined — i.e. for non-HTTP errors that bypass the
// interceptor (e.g. a Zod parse failure thrown from inside the queryFn).
// ---------------------------------------------------------------------------

function getRevenueErrorCopy(error: Error): string {
  const status = (error as Partial<ApiError>).statusCode;
  if (
    status === 403 ||
    (status === undefined && error.message === 'Request failed with status code 403')
  ) {
    return 'You do not have permission to view revenue.';
  }
  if (status === 502 || (status === undefined && error.message?.includes('502'))) {
    return 'Revenue service unavailable upstream. Please retry shortly.';
  }
  return 'Revenue service unavailable. Try again later.';
}

// ---------------------------------------------------------------------------
// Date range picker — simple native inputs (no extra dependency)
// ---------------------------------------------------------------------------

interface DateRangeFilterProps {
  dateRange: RevenueDateRange;
  onChange: (range: RevenueDateRange) => void;
}

function DateRangeFilter({ dateRange, onChange }: DateRangeFilterProps) {
  return (
    <div className="flex items-center gap-3" data-testid="date-range-filter">
      <label className="flex items-center gap-1.5 text-[13px] text-[#666]">
        From
        <input
          type="date"
          value={dateRange.from}
          onChange={(e) => onChange({ ...dateRange, from: e.target.value })}
          className="rounded-md border border-[#e0e0e0] px-2 py-1.5 text-[13px] text-[#333] focus:border-[#6B0F1A] focus:outline-none focus:ring-1 focus:ring-[#6B0F1A]"
          aria-label="Start date"
          data-testid="date-from"
        />
      </label>
      <label className="flex items-center gap-1.5 text-[13px] text-[#666]">
        To
        <input
          type="date"
          value={dateRange.to}
          onChange={(e) => onChange({ ...dateRange, to: e.target.value })}
          className="rounded-md border border-[#e0e0e0] px-2 py-1.5 text-[13px] text-[#333] focus:border-[#6B0F1A] focus:outline-none focus:ring-1 focus:ring-[#6B0F1A]"
          aria-label="End date"
          data-testid="date-to"
        />
      </label>
    </div>
  );
}

// ---------------------------------------------------------------------------
// KPI skeleton
// ---------------------------------------------------------------------------

function KPISkeleton() {
  return (
    <div
      className="animate-pulse rounded-[14px] border-[1.5px] border-[#f0f0f0] bg-white px-6 py-7 text-center shadow-[0_2px_12px_rgba(0,0,0,0.06)]"
      role="status"
      aria-label="Loading KPI"
    >
      <div className="mx-auto mb-3 h-7 w-7 rounded-full bg-[#f0f0f0]" />
      <div className="mx-auto mb-2 h-8 w-20 rounded bg-[#f0f0f0]" />
      <div className="mx-auto h-3 w-24 rounded bg-[#f0f0f0]" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chart skeleton
// ---------------------------------------------------------------------------

function ChartSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      {[0, 1].map((i) => (
        <div
          key={i}
          className="flex h-[350px] items-center justify-center rounded-[14px] border-[1.5px] border-[#f0f0f0] bg-white shadow-[0_2px_12px_rgba(0,0,0,0.06)]"
          role="status"
          aria-label="Loading chart"
        >
          <LoadingSpinner size="md" text="Loading chart..." />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export function RevenuePage() {
  const { data, isLoading, isFetching, error, dateRange, setDateRange, refresh } = useRevenue();

  const totalRevenue = data?.total_revenue ?? 0;
  const wireFees = data?.wire_transfer_fees ?? 0;
  const fxMargins = data?.fx_margins ?? 0;
  const details = data?.details ?? [];

  return (
    <ErrorBoundary>
      <div className="space-y-6" data-testid="revenue-page">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-[22px] font-bold text-[#333]">Revenue</h1>
            <p className="mt-0.5 text-[13px] text-[#999]">
              Revenue breakdown across wire transfer fees and FX margins
            </p>
          </div>
          <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center">
            <DateRangeFilter dateRange={dateRange} onChange={setDateRange} />
            <Button
              variant="outline"
              size="sm"
              onClick={refresh}
              disabled={isFetching}
              aria-label="Refresh revenue data"
              data-testid="refresh-button"
            >
              {isFetching ? (
                <span className="flex items-center gap-2">
                  <LoadingSpinner size="sm" />
                  Refreshing...
                </span>
              ) : (
                'Refresh'
              )}
            </Button>
          </div>
        </div>

        {/* Error state */}
        {error && !isLoading && (
          <div
            className="rounded-lg border border-[#ffcdd2] bg-[#ffebee] px-4 py-3 text-sm text-[#c62828]"
            role="alert"
            data-testid="revenue-error"
          >
            {getRevenueErrorCopy(error)}
            <button
              onClick={refresh}
              className="ml-2 font-semibold underline"
              data-testid="retry-button"
            >
              Retry
            </button>
          </div>
        )}

        {/* KPI cards */}
        {isLoading && !data ? (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3" aria-label="Loading revenue KPIs">
            <KPISkeleton />
            <KPISkeleton />
            <KPISkeleton />
          </div>
        ) : (
          <div
            className="grid grid-cols-1 gap-5 sm:grid-cols-3"
            aria-label="Revenue KPIs"
            data-testid="revenue-kpis"
          >
            <KPICard value={formatCurrency(totalRevenue)} label="Total Revenue" />
            <KPICard value={formatCurrency(wireFees)} label="Wire Transfer Fees" />
            <KPICard value={formatCurrency(fxMargins)} label="FX Margins" />
          </div>
        )}

        {/* Wallet balances panel hidden 2026-05-04 — multi-wallet de-scoped from MVP. */}
        {/* Restore via `<WalletBalancesPanel />` + the import + the WALLET_BALANCES_KEY */}
        {/* invalidation in useRevenue.ts when re-enabling. */}

        {/* Charts */}
        {isLoading && !data ? (
          <ChartSkeleton />
        ) : (
          <RevenueCharts wireFees={wireFees} fxMargins={fxMargins} />
        )}

        {/* Breakdown table */}
        {!error && <RevenueBreakdown details={details} isLoading={isLoading && !data} />}

        {/* Empty state for zero revenue */}
        {!isLoading && !error && totalRevenue === 0 && details.length === 0 && (
          <div
            className="rounded-lg border border-[#e0e0e0] bg-white px-4 py-12 text-center text-sm text-[#999]"
            data-testid="revenue-empty"
          >
            No revenue recorded yet
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
}
